"use client";

import { useEffect, useState } from "react";
import { Check, X, Minus, Loader, Download } from "lucide-react";
import MainLayout from "../../../components/layout/MainLayout";
import { getSafeJobAnalysisById } from "../../../../Services/safeJobAnalysisService";
import { useParams } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Dynamically formats checklist item names for display
 * Converts technical names to user-friendly display names
 * @param {string} name - The raw checklist item name
 * @returns {string} - Formatted display name
 */
const formatChecklistItemName = (name) => {
  if (!name || typeof name !== "string") {
    return "Unnamed Item";
  }

  return (
    name
      .toLowerCase()
      .trim()
      // Remove common prefixes
      .replace(/^(sja_|checklist_|item_)/, "")
      // Handle common abbreviations and technical terms
      .replace(/ppe/g, "personal protective equipment")
      .replace(/eq/g, "equipment")
      .replace(/temp/g, "temperature")
      .replace(/max/g, "maximum")
      .replace(/min/g, "minimum")
      .replace(/std/g, "standard")
      .replace(/req/g, "required")
      .replace(/mgmt/g, "management")
      .replace(/ctrl/g, "control")
      // Fix common typos
      .replace(/glassess/g, "glasses")
      .replace(/cloths/g, "clothing")
      .replace(/equipement/g, "equipment")
      // Split on common separators
      .split(/[_\s-]+/)
      // Capitalize each word
      .map((word) => {
        if (word.length === 0) return "";
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .filter((word) => word.length > 0)
      .join(" ")
  );
};

export default function SafeJobAnalysisDetailPage() {
  const [showChecklists, setShowChecklists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    projectDetail: {},
    signature: { person: [], customer: {}, approver: {} },
    scaffoldData: [],
    protectiveEquipment: [],
    typeOfWork: [],
    designatedWorkArea: [],
    emergencyProcedure: "",
  });

  const { sjaId } = useParams();

  useEffect(() => {
    if (sjaId) {
      getSafeJobAnalysisDataById(sjaId);
    } else {
      setError("No Safe Job Analysis ID provided");
      setLoading(false);
    }
  }, [sjaId]);

  const getSafeJobAnalysisDataById = async (safeJobAnalysisId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSafeJobAnalysisById(safeJobAnalysisId);
      if (!response || !response.data) {
        throw new Error("No data received from server");
      }

      // Process the response data with defaults to prevent undefined errors
      const responseData = response.data || {};

      // Set data with proper defaults to prevent undefined errors
      setData({
        projectDetail: responseData.projectDetail || {},
        projectName: responseData.projectId?.projectName || "N/A",
        projectNumber: responseData.projectId?.projectNumber || "N/A",
        signature: {
          person: responseData.signature?.person || [],
          customer: responseData.signature?.customer || {},
          approver: responseData.signature?.approver || {},
        },
        scaffoldData: responseData.scaffoldData || [],
        protectiveEquipment: responseData.protectiveEquipment || [],
        typeOfWork: responseData.typeOfWork || [],
        designatedWorkArea: responseData.designatedWorkArea || [],
        emergencyProcedure: responseData.emergencyProcedure || "",
      });
    } catch (error) {
      console.error("Error fetching SJA data:", error);
      setError(error.message || "Failed to load Safe Job Analysis data");
    } finally {
      setLoading(false);
    }
  };

  const renderRiskLevel = (level) => {
    if (!level) return null;

    if (level === "low")
      return (
        <span
          className="inline-block w-3 h-3 bg-green-500 rounded-full mr-1"
          title="Low Risk"
        />
      );
    if (level === "medium")
      return (
        <span
          className="inline-block w-3 h-3 bg-yellow-500 rounded-full mr-1"
          title="Medium Risk"
        />
      );
    if (level === "high")
      return (
        <span
          className="inline-block w-3 h-3 bg-red-500 rounded-full mr-1"
          title="High Risk"
        />
      );
    return null;
  };

  const generatePDF = () => {
    try {
      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      let yPosition = 20;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredSpace = 20) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          doc.addPage();
          yPosition = 20;
          return true;
        }
        return false;
      };

      // Helper function to add header on each page
      const addHeader = () => {
        doc.setFillColor(41, 98, 176);
        doc.rect(0, 0, pageWidth, 30, "F");

        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text("SAFE JOB ANALYSIS", pageWidth / 2, 20, { align: "center" });

        yPosition = 40;
      };

      // Add initial header
      addHeader();

      // Project Information Section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("PROJECT INFORMATION", 14, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const projectInfo = [
        ["Project Name", data?.projectName || "N/A"],
        ["Project Number", data?.projectNumber || "N/A"],
        [
          "SJA Name",
          data.projectDetail?.safeJobAnalysisName || "Not specified",
        ],
        [
          "Date of Analysis",
          data.projectDetail?.dateOfAnalysis || "Not specified",
        ],
      ];

      doc.autoTable({
        startY: yPosition,
        head: [["Field", "Value"]],
        body: projectInfo,
        theme: "grid",
        headStyles: {
          fillColor: [52, 152, 219],
          textColor: 255,
          fontStyle: "bold",
        },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: { 0: { fontStyle: "bold", fillColor: [236, 240, 241] } },
      });

      yPosition = doc.lastAutoTable.finalY + 15;

      // Work Description Section
      checkPageBreak(30);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("WORK DESCRIPTION", 14, yPosition);
      yPosition += 8;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const workDescription =
        data.projectDetail?.workDescription || "No work description provided";
      const splitDescription = doc.splitTextToSize(
        workDescription,
        pageWidth - 28
      );
      doc.text(splitDescription, 14, yPosition);
      yPosition += splitDescription.length * 4 + 10;

      // Responsible Workers Section
      if (data.signature?.person && data.signature.person.length > 0) {
        checkPageBreak(40);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("RESPONSIBLE WORKERS", 14, yPosition);
        yPosition += 10;

        const workerData = data.signature.person.map((worker, index) => [
          index + 1,
          worker?.name || "Unknown",
          worker?.date || "No date",
          worker?.signatureDataUrl ? "Signed" : "Not signed",
        ]);

        doc.autoTable({
          startY: yPosition,
          head: [["No.", "Name", "Date", "Signature Status"]],
          body: workerData,
          theme: "striped",
          headStyles: {
            fillColor: [46, 125, 50],
            textColor: 255,
            fontStyle: "bold",
          },
          styles: { fontSize: 9, cellPadding: 3 },
          alternateRowStyles: { fillColor: [245, 245, 245] },
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Tasks and Risk Assessment Section
      if (data.scaffoldData && data.scaffoldData.length > 0) {
        checkPageBreak(40);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("TASKS AND RISK ASSESSMENT", 14, yPosition);
        yPosition += 10;

        const taskData = [];
        data.scaffoldData.forEach((task) => {
          if (task?.subtaskData && task.subtaskData.length > 0) {
            task.subtaskData.forEach((subData, index) => {
              taskData.push([
                index === 0 ? task?.subtask || "Unnamed task" : "",
                subData?.risk || "No risk specified",
                subData?.riskLevel || "N/A",
                subData?.measure || "No measure specified",
                subData?.measureLevel || "N/A",
              ]);
            });
          } else {
            taskData.push([
              task?.subtask || "Unnamed task",
              "No risks identified",
              "N/A",
              "No measures provided",
              "N/A",
            ]);
          }
        });

        doc.autoTable({
          startY: yPosition,
          head: [
            ["Task", "Risk", "Risk Level", "Control Measure", "Measure Level"],
          ],
          body: taskData,
          theme: "grid",
          headStyles: {
            fillColor: [244, 67, 54],
            textColor: 255,
            fontStyle: "bold",
          },
          styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
          columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 45 },
            2: { cellWidth: 20 },
            3: { cellWidth: 45 },
            4: { cellWidth: 20 },
          },
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Safety Checklists Section - Only include if data exists
      const checklists = [
        {
          title: "PROTECTIVE EQUIPMENT CHECKLIST",
          data: data.protectiveEquipment,
          color: [76, 175, 80],
        },
        {
          title: "REQUIRED PERMITS CHECKLIST",
          data: data.typeOfWork,
          color: [255, 152, 0],
        },
        {
          title: "DESIGNATED WORK AREA CHECKLIST",
          data: data.designatedWorkArea,
          color: [156, 39, 176],
        },
      ];

      // Filter out checklists that have no data
      const checklistsWithData = checklists.filter(
        (checklist) =>
          checklist.data &&
          checklist.data.length > 0 &&
          checklist.data.some(
            (item) => item && (item.yes || item.no || item.na)
          )
      );

      checklistsWithData.forEach((checklist) => {
        checkPageBreak(40);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text(checklist.title, 14, yPosition);
        yPosition += 10;

        const checklistData = checklist.data.map((item) => [
          formatChecklistItemName(item?.checkListName),
          item?.yes ? "✓" : "",
          item?.no ? "✗" : "",
          item?.na ? "N/A" : "",
        ]);

        doc.autoTable({
          startY: yPosition,
          head: [["Item", "Yes", "No", "N/A"]],
          body: checklistData,
          theme: "striped",
          headStyles: {
            fillColor: checklist.color,
            textColor: 255,
            fontStyle: "bold",
          },
          styles: { fontSize: 9, cellPadding: 3, halign: "center" },
          columnStyles: {
            0: { halign: "left", cellWidth: 120 },
            1: { cellWidth: 20 },
            2: { cellWidth: 20 },
            3: { cellWidth: 20 },
          },
          alternateRowStyles: { fillColor: [250, 250, 250] },
        });

        yPosition = doc.lastAutoTable.finalY + 15;
      });

      // Emergency Procedure Section - Only include if data exists
      if (data.emergencyProcedure && data.emergencyProcedure.trim() !== "") {
        checkPageBreak(30);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("EMERGENCY PROCEDURE", 14, yPosition);
        yPosition += 8;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        const emergencyText = doc.splitTextToSize(
          data.emergencyProcedure,
          pageWidth - 28
        );
        doc.text(emergencyText, 14, yPosition);
        yPosition += emergencyText.length * 4 + 15;
      }

      // Signatures Section - Only include if signatures exist
      const hasCustomerSignature = data.signature?.customer?.signature;
      const hasApproverSignature = data.signature?.approver?.signature;
      const hasCustomerName = data.signature?.customer?.name;
      const hasApproverName = data.signature?.approver?.name;

      if (
        hasCustomerSignature ||
        hasApproverSignature ||
        hasCustomerName ||
        hasApproverName
      ) {
        checkPageBreak(50);
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.text("SIGNATURES", 14, yPosition);
        yPosition += 15;

        const signatureData = [];

        // Only add customer row if there's customer data
        if (hasCustomerName || hasCustomerSignature) {
          signatureData.push([
            "Customer",
            data.signature?.customer?.name || "Not provided",
            data.signature?.customer?.signature ? "Signed" : "Not signed",
          ]);
        }

        // Only add approver row if there's approver data
        if (hasApproverName || hasApproverSignature) {
          signatureData.push([
            "Team Leader",
            data.signature?.approver?.name || "Not provided",
            data.signature?.approver?.signature ? "Signed" : "Not signed",
          ]);
        }

        if (signatureData.length > 0) {
          doc.autoTable({
            startY: yPosition,
            head: [["Role", "Name", "Status"]],
            body: signatureData,
            theme: "grid",
            headStyles: {
              fillColor: [63, 81, 181],
              textColor: 255,
              fontStyle: "bold",
            },
            styles: { fontSize: 10, cellPadding: 5 },
            columnStyles: { 1: { fontStyle: "bold" } },
          });
        }
      }

      // Footer on each page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
          `Generated on: ${new Date().toLocaleDateString()} | Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: "center" }
        );
      }

      // Generate filename with timestamp
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      const filename = `SJA_${
        data?.projectName?.replace(/[^a-zA-Z0-9]/g, "_") || "Report"
      }_${timestamp}.pdf`;

      doc.save(filename);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Loading state
  if (loading) {
    return (
      <MainLayout title="Loading Safe Job Analysis...">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <Loader className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading Safe Job Analysis data...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <MainLayout title="Error - Safe Job Analysis">
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
            <div className="flex items-center justify-center mb-4">
              <X className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-center text-gray-900 mb-2">
              Error Loading Data
            </h2>
            <p className="text-gray-600 text-center mb-6">{error}</p>
            <div className="flex justify-center">
              <button
                onClick={() => getSafeJobAnalysisDataById(sjaId)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Safe Job Analysis Detail Page">
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <h1 className="text-xl font-bold text-gray-900 uppercase">
                Safe Job Analysis
              </h1>
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                <div className="flex-1 min-w-[280px] bg-white border border-gray-200 rounded-md px-4 py-3">
                  <p className="text-sm font-medium text-gray-500 uppercase">
                    Project Name
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    {data?.projectName || "N/A"}
                  </p>
                </div>

                <div className="flex-1 min-w-[200px] bg-white border border-gray-200 rounded-md px-4 py-3">
                  <p className="text-sm font-medium text-gray-500 uppercase">
                    Project Number
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    {data?.projectNumber || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              Safe Job Analysis
            </h1>
            <button
              onClick={generatePDF}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md hover:shadow-lg"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
          </div>

          {/* Project Details Section */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Project Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Safe Job Analysis Name
                  </label>
                  <div className="bg-gray-50 rounded-md px-4 py-3 border border-gray-200">
                    <p className="text-gray-900">
                      {data.projectDetail?.safeJobAnalysisName ||
                        "Not specified"}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Analysis
                  </label>
                  <div className="bg-gray-50 rounded-md px-4 py-3 border border-gray-200">
                    <p className="text-gray-900">
                      {data.projectDetail?.dateOfAnalysis || "Not specified"}
                    </p>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Description
                  </label>
                  <div className="bg-gray-50 rounded-md px-4 py-3 border border-gray-200 min-h-[100px]">
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {data.projectDetail?.workDescription ||
                        "No work description provided"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Responsible Workers Section */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Responsible Workers
              </h2>

              {data.signature?.person && data.signature.person.length > 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200">
                    <div className="px-4 py-3 text-sm font-medium text-gray-700">
                      Name
                    </div>
                    <div className="px-4 py-3 text-sm font-medium text-gray-700">
                      Date
                    </div>
                    <div className="px-4 py-3 text-sm font-medium text-gray-700">
                      Signature
                    </div>
                  </div>

                  {data.signature.person.map((worker, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-3 border-b border-gray-200 last:border-b-0"
                    >
                      <div className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {worker?.name || "Unknown"}
                      </div>
                      <div className="px-4 py-3 text-sm text-gray-900">
                        {worker?.date || "No date"}
                      </div>
                      <div className="px-4 py-3">
                        {worker?.signatureDataUrl ? (
                          <img
                            src={worker.signatureDataUrl}
                            alt={`${worker.name || "Worker"}'s signature`}
                            className="h-8 object-contain"
                          />
                        ) : (
                          <span className="text-gray-400 text-sm italic">
                            No signature
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-500">
                    No responsible workers assigned
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Tasks and Risk Assessment Section */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Tasks and Risk Assessment
              </h2>

              {data.scaffoldData && data.scaffoldData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Subtask
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Risk
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Measure
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {data.scaffoldData.map((task, taskIndex) => (
                        <tr
                          key={taskIndex}
                          className={
                            taskIndex % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {task?.subtask || "Unnamed task"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <ul className="space-y-2">
                              {task?.subtaskData &&
                              task.subtaskData.length > 0 ? (
                                task.subtaskData.map((data, dataIndex) => (
                                  <li
                                    key={dataIndex}
                                    className="flex items-center"
                                  >
                                    {renderRiskLevel(data?.riskLevel)}
                                    <span>
                                      {data?.risk || "No risk specified"}
                                    </span>
                                  </li>
                                ))
                              ) : (
                                <li className="text-gray-400 italic">
                                  No risks identified
                                </li>
                              )}
                            </ul>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <ul className="space-y-2">
                              {task?.subtaskData &&
                              task.subtaskData.length > 0 ? (
                                task.subtaskData.map((data, dataIndex) => (
                                  <li
                                    key={dataIndex}
                                    className="flex items-center"
                                  >
                                    {renderRiskLevel(data?.measureLevel)}
                                    <span>
                                      {data?.measure || "No measure specified"}
                                    </span>
                                  </li>
                                ))
                              ) : (
                                <li className="text-gray-400 italic">
                                  No measures provided
                                </li>
                              )}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-gray-500">No tasks added</p>
                </div>
              )}
            </div>
          </div>

          {/* Checklists Toggle Button */}
          <button
            onClick={() => setShowChecklists(!showChecklists)}
            className="mb-6 font-medium px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            {showChecklists ? "Hide Checklists" : "Show Checklists"}
          </button>

          {/* Safety Checklists Section */}
          {showChecklists && (
            <div className="bg-white shadow rounded-lg overflow-hidden mb-8 transition-all duration-300">
              <div className="p-6 space-y-8">
                {/* Protective Equipment Checklist */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Protective Equipment
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Item
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            Yes
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            No
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            N/A
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.protectiveEquipment &&
                        data.protectiveEquipment.length > 0 ? (
                          data.protectiveEquipment.map((item, index) => (
                            <tr
                              key={index}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {formatChecklistItemName(item?.checkListName)}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.yes && (
                                  <Check className="h-5 w-5 text-green-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.no && (
                                  <X className="h-5 w-5 text-red-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.na && (
                                  <Minus className="h-5 w-5 text-gray-400 mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-6 py-4 text-center text-gray-500"
                            >
                              No protective equipment data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Required Permits Checklist */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Required Permits
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Item
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            Yes
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            No
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            N/A
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.typeOfWork && data.typeOfWork.length > 0 ? (
                          data.typeOfWork.map((item, index) => (
                            <tr
                              key={index}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {formatChecklistItemName(item?.checkListName)}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.yes && (
                                  <Check className="h-5 w-5 text-green-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.no && (
                                  <X className="h-5 w-5 text-red-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.na && (
                                  <Minus className="h-5 w-5 text-gray-400 mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-6 py-4 text-center text-gray-500"
                            >
                              No permit data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Designated Work Area Checklist */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Designated Work Area
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            Item
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            Yes
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            No
                          </th>
                          <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                          >
                            N/A
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data.designatedWorkArea &&
                        data.designatedWorkArea.length > 0 ? (
                          data.designatedWorkArea.map((item, index) => (
                            <tr
                              key={index}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                {formatChecklistItemName(item?.checkListName)}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.yes && (
                                  <Check className="h-5 w-5 text-green-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.no && (
                                  <X className="h-5 w-5 text-red-500 mx-auto" />
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {item?.na && (
                                  <Minus className="h-5 w-5 text-gray-400 mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan="4"
                              className="px-6 py-4 text-center text-gray-500"
                            >
                              No work area data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Emergency Procedure Section */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Emergency Procedure
              </h2>

              <div className="bg-gray-50 rounded-md px-4 py-3 border border-gray-200 min-h-[100px]">
                {data.emergencyProcedure ? (
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {data.emergencyProcedure}
                  </p>
                ) : (
                  <p className="text-gray-500 italic">
                    No emergency procedure specified
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Signatures Section */}
          <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6">
                Signatures
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="flex flex-col items-center">
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 w-full flex justify-center mb-4">
                    {data.signature?.customer?.signature ? (
                      <img
                        src={data.signature.customer.signature}
                        alt="Customer Signature"
                        className="h-20 object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/placeholder.svg";
                        }}
                      />
                    ) : (
                      <div className="h-20 flex items-center justify-center text-gray-400">
                        No signature available
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    {data.signature?.customer?.name || "Customer"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Customer's Signature
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4 w-full flex justify-center mb-4">
                    {data.signature?.approver?.signature ? (
                      <img
                        src={data.signature.approver.signature}
                        alt="Approver Signature"
                        className="h-20 object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/placeholder.svg";
                        }}
                      />
                    ) : (
                      <div className="h-20 flex items-center justify-center text-gray-400">
                        No signature available
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-700">
                    {data.signature?.approver?.name || "Team Leader"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Team Leader's Signature
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </MainLayout>
  );
}
